import { useState, useEffect } from 'react';
import { useAuth } from '@/lib/auth-context';
import { branchAPI, leaveTypeAPI } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Settings, Clock, FileText, Plus, Trash2, Pencil, CalendarDays, Plug } from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import { MarketingIntegrationCard } from '@/components/settings/MarketingIntegrationCard';

const DAYS_OF_WEEK = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
const DAY_LABELS = { monday: 'Monday', tuesday: 'Tuesday', wednesday: 'Wednesday', thursday: 'Thursday', friday: 'Friday', saturday: 'Saturday', sunday: 'Sunday' };
const DEFAULT_SCHEDULE = {
  monday:    { is_working: true, start_time: '09:30', end_time: '18:00' },
  tuesday:   { is_working: true, start_time: '09:30', end_time: '18:00' },
  wednesday: { is_working: true, start_time: '09:30', end_time: '18:00' },
  thursday:  { is_working: true, start_time: '09:30', end_time: '18:00' },
  friday:    { is_working: true, start_time: '09:30', end_time: '18:00' },
  saturday:  { is_working: false, start_time: '', end_time: '' },
  sunday:    { is_working: false, start_time: '', end_time: '' },
};

export default function SettingsPage() {
  const { user, isSuperUser } = useAuth();
  const [branches, setBranches] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState('');
  const [branchData, setBranchData] = useState(null);
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [ltDialogOpen, setLtDialogOpen] = useState(false);
  const [editLt, setEditLt] = useState(null);
  const [ltForm, setLtForm] = useState({ name: '', total_days: 14 });
  const [branchForm, setBranchForm] = useState({});
  const [workSchedule, setWorkSchedule] = useState(DEFAULT_SCHEDULE);

  useEffect(() => { loadBranches(); }, []);

  useEffect(() => {
    if (selectedBranch) {
      loadBranchData();
      loadLeaveTypes();
    }
  }, [selectedBranch]);

  const loadBranches = async () => {
    try {
      const res = await branchAPI.list();
      setBranches(res.data);
      if (res.data.length > 0) {
        setSelectedBranch(user?.branch_id || res.data[0].id);
      }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const loadBranchData = async () => {
    try {
      const res = await branchAPI.get(selectedBranch);
      setBranchData(res.data);
      setBranchForm({
        currency: res.data.currency || 'CAD',
        office_start_time: res.data.office_start_time,
        grace_minutes: res.data.grace_minutes,
        lates_per_day_deduction: res.data.lates_per_day_deduction,
        leave_escalation_days: res.data.leave_escalation_days ?? 2,
        leave_escalation_overlap: res.data.leave_escalation_overlap ?? 1
      });
      setWorkSchedule(res.data.work_schedule || DEFAULT_SCHEDULE);
    } catch (e) { console.error(e); }
  };

  const loadLeaveTypes = async () => {
    try {
      const res = await leaveTypeAPI.list(selectedBranch);
      setLeaveTypes(res.data);
    } catch (e) { console.error(e); }
  };

  const saveBranchSettings = async () => {
    try {
      await branchAPI.update(selectedBranch, { ...branchForm, work_schedule: workSchedule });
      toast.success('Branch settings updated');
      loadBranchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Update failed');
    }
  };

  const updateDaySchedule = (day, field, value) => {
    setWorkSchedule(prev => ({
      ...prev,
      [day]: { ...prev[day], [field]: value }
    }));
  };

  const handleSaveLeaveType = async () => {
    if (!ltForm.name) { toast.error('Name is required'); return; }
    try {
      if (editLt) {
        await leaveTypeAPI.update(editLt.id, ltForm);
        toast.success('Leave type updated');
      } else {
        await leaveTypeAPI.create({ ...ltForm, branch_id: selectedBranch });
        toast.success('Leave type created');
      }
      setLtDialogOpen(false);
      setEditLt(null);
      loadLeaveTypes();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    }
  };

  const handleDeleteLeaveType = async (id) => {
    try {
      await leaveTypeAPI.delete(id);
      toast.success('Leave type removed');
      loadLeaveTypes();
    } catch { toast.error('Delete failed'); }
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-heading font-semibold tracking-tight">Branch Settings</h1>
          <p className="text-muted-foreground">Configure office timings, leave policies, and rules</p>
        </div>
        <Select value={selectedBranch} onValueChange={setSelectedBranch}>
          <SelectTrigger className="w-[220px]">
            <SelectValue placeholder="Select branch" />
          </SelectTrigger>
          <SelectContent>
            {branches.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <Tabs defaultValue="timings">
        <TabsList>
          <TabsTrigger value="timings" className="gap-1.5"><Clock size={14} /> Timings & Policy</TabsTrigger>
          <TabsTrigger value="leave-types" className="gap-1.5"><FileText size={14} /> Leave Types</TabsTrigger>
          {isSuperUser && <TabsTrigger value="integrations" className="gap-1.5" data-testid="tab-integrations"><Plug size={14} /> Integrations</TabsTrigger>}
        </TabsList>

        <TabsContent value="timings" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg font-heading">Office Timings & Late Policy</CardTitle>
              <CardDescription>Configure when the office opens and how lates affect payroll</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                <div className="space-y-2">
                  <Label>Currency</Label>
                  <Select value={branchForm.currency || 'CAD'} onValueChange={v => setBranchForm({...branchForm, currency: v})}>
                    <SelectTrigger data-testid="settings-currency-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="INR">INR (₹)</SelectItem>
                      <SelectItem value="CAD">CAD (C$)</SelectItem>
                      <SelectItem value="USD">USD ($)</SelectItem>
                      <SelectItem value="GBP">GBP (£)</SelectItem>
                      <SelectItem value="EUR">EUR (€)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Office Start Time</Label>
                  <Input
                    type="time"
                    value={branchForm.office_start_time || ''}
                    onChange={e => setBranchForm({...branchForm, office_start_time: e.target.value})}
                    data-testid="settings-start-time-input"
                  />
                  <p className="text-xs text-muted-foreground">Employees checking in after this time are marked late</p>
                </div>
                <div className="space-y-2">
                  <Label>Grace Period (minutes)</Label>
                  <Input
                    type="number"
                    value={branchForm.grace_minutes || 0}
                    onChange={e => setBranchForm({...branchForm, grace_minutes: parseInt(e.target.value) || 0})}
                    data-testid="settings-grace-input"
                  />
                  <p className="text-xs text-muted-foreground">Check-in within grace period counts as late but NOT half-day. Beyond grace = half-day.</p>
                </div>
                <div className="space-y-2">
                  <Label>Lates per Day Deduction</Label>
                  <Input
                    type="number"
                    value={branchForm.lates_per_day_deduction || 0}
                    onChange={e => setBranchForm({...branchForm, lates_per_day_deduction: parseInt(e.target.value) || 1})}
                    data-testid="settings-lates-deduction-input"
                  />
                  <p className="text-xs text-muted-foreground">
                    {branchForm.lates_per_day_deduction || 0} lates = 1 full day salary deduction
                  </p>
                </div>
              </div>

              {/* Leave Escalation Settings */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-4 border-t">
                <div className="space-y-2">
                  <Label>Leave Escalation — Days Threshold</Label>
                  <Input
                    type="number"
                    value={branchForm.leave_escalation_days ?? 2}
                    onChange={e => setBranchForm({...branchForm, leave_escalation_days: parseInt(e.target.value) || 1})}
                    data-testid="settings-escalation-days-input"
                  />
                  <p className="text-xs text-muted-foreground">
                    Leaves exceeding {branchForm.leave_escalation_days ?? 2} days will be escalated to Super User for approval
                  </p>
                </div>
                <div className="space-y-2">
                  <Label>Leave Escalation — Staff Overlap Threshold</Label>
                  <Input
                    type="number"
                    value={branchForm.leave_escalation_overlap ?? 1}
                    onChange={e => setBranchForm({...branchForm, leave_escalation_overlap: parseInt(e.target.value) || 1})}
                    data-testid="settings-escalation-overlap-input"
                  />
                  <p className="text-xs text-muted-foreground">
                    If more than {branchForm.leave_escalation_overlap ?? 1} staff member(s) already on leave for the same dates, escalate to Super User
                  </p>
                </div>
              </div>

              {/* Work Schedule Editor */}
              <div className="space-y-3">
                <div className="flex items-center gap-2 mb-2">
                  <CalendarDays size={16} className="text-primary" />
                  <p className="text-sm font-semibold">Weekly Work Schedule</p>
                </div>
                <div className="rounded-lg border overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/50">
                      <tr>
                        <th className="text-left p-3 font-medium">Day</th>
                        <th className="text-center p-3 font-medium">Working</th>
                        <th className="text-center p-3 font-medium">Start Time</th>
                        <th className="text-center p-3 font-medium">End Time</th>
                      </tr>
                    </thead>
                    <tbody>
                      {DAYS_OF_WEEK.map(day => {
                        const sched = workSchedule[day] || { is_working: false, start_time: '', end_time: '' };
                        return (
                          <tr key={day} className="border-t hover:bg-muted/30 transition-colors">
                            <td className="p-3 font-medium">{DAY_LABELS[day]}</td>
                            <td className="p-3 text-center">
                              <Switch
                                checked={sched.is_working}
                                onCheckedChange={v => updateDaySchedule(day, 'is_working', v)}
                                data-testid={`schedule-${day}-toggle`}
                              />
                            </td>
                            <td className="p-3 text-center">
                              {sched.is_working ? (
                                <Input
                                  type="time"
                                  value={sched.start_time}
                                  onChange={e => updateDaySchedule(day, 'start_time', e.target.value)}
                                  className="h-8 w-28 mx-auto text-center"
                                  data-testid={`schedule-${day}-start`}
                                />
                              ) : (
                                <span className="text-muted-foreground">—</span>
                              )}
                            </td>
                            <td className="p-3 text-center">
                              {sched.is_working ? (
                                <Input
                                  type="time"
                                  value={sched.end_time}
                                  onChange={e => updateDaySchedule(day, 'end_time', e.target.value)}
                                  className="h-8 w-28 mx-auto text-center"
                                  data-testid={`schedule-${day}-end`}
                                />
                              ) : (
                                <span className="text-muted-foreground">—</span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <p className="text-xs text-muted-foreground">
                  Working days: {DAYS_OF_WEEK.filter(d => workSchedule[d]?.is_working).map(d => DAY_LABELS[d].slice(0, 3)).join(', ')} ({DAYS_OF_WEEK.filter(d => workSchedule[d]?.is_working).length} days/week)
                </p>
              </div>

              {/* Policy Preview */}
              <div className="bg-muted/50 rounded-lg p-4 border">
                <p className="text-sm font-medium mb-2">Policy Preview</p>
                <ul className="text-sm text-muted-foreground space-y-1">
                  <li>Office opens at <strong>{branchForm.office_start_time || '09:30'}</strong></li>
                  <li>Late after start time, half-day if more than <strong>{branchForm.grace_minutes || 20} minutes</strong> late</li>
                  <li>Every <strong>{branchForm.lates_per_day_deduction || 3} lates</strong> = 1 full day salary deduction</li>
                  <li>Leaves &gt; <strong>{branchForm.leave_escalation_days ?? 2} days</strong> → Escalated to Super User</li>
                  <li>If &gt; <strong>{branchForm.leave_escalation_overlap ?? 1} staff</strong> on leave same dates → Escalated to Super User</li>
                  <li>All <strong>manager leaves</strong> → Always go to Super User</li>
                </ul>
              </div>

              <Button onClick={saveBranchSettings} data-testid="settings-save-button">Save Settings</Button>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="leave-types" className="mt-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="text-lg font-heading">Leave Types</CardTitle>
                <CardDescription>Define leave categories and annual quotas</CardDescription>
              </div>
              <Button size="sm" onClick={() => { setEditLt(null); setLtForm({ name: '', total_days: 14 }); setLtDialogOpen(true); }} className="gap-1" data-testid="add-leave-type-button">
                <Plus size={14} /> Add Type
              </Button>
            </CardHeader>
            <CardContent>
              {leaveTypes.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">No leave types configured. Add leave types like Casual Leave, Medical Leave etc.</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Leave Type</TableHead>
                      <TableHead className="text-right">Annual Days</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {leaveTypes.map(lt => (
                      <TableRow key={lt.id}>
                        <TableCell className="font-medium">{lt.name}</TableCell>
                        <TableCell className="text-right tabular-nums">{lt.total_days}</TableCell>
                        <TableCell>{lt.is_active ? 'Active' : 'Inactive'}</TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <Button variant="ghost" size="sm" onClick={() => { setEditLt(lt); setLtForm({ name: lt.name, total_days: lt.total_days }); setLtDialogOpen(true); }}>
                              <Pencil size={14} />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => handleDeleteLeaveType(lt.id)} className="text-destructive">
                              <Trash2 size={14} />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>

          <Dialog open={ltDialogOpen} onOpenChange={setLtDialogOpen}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle className="font-heading">{editLt ? 'Edit Leave Type' : 'Add Leave Type'}</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Leave Type Name</Label>
                  <Input value={ltForm.name} onChange={e => setLtForm({...ltForm, name: e.target.value})} placeholder="e.g., Casual Leave" data-testid="leave-type-name-input" />
                </div>
                <div className="space-y-2">
                  <Label>Annual Days</Label>
                  <Input type="number" value={ltForm.total_days} onChange={e => setLtForm({...ltForm, total_days: parseInt(e.target.value) || 0})} data-testid="leave-type-days-input" />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setLtDialogOpen(false)}>Cancel</Button>
                <Button onClick={handleSaveLeaveType} data-testid="leave-type-save-button">{editLt ? 'Update' : 'Create'}</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </TabsContent>

        {isSuperUser && (
          <TabsContent value="integrations" className="mt-4">
            <MarketingIntegrationCard />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}
